import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Loader2, Copy, QrCode, Lock, Download, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const accent = "#A3E635";
const inp = "w-full bg-black border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#A3E635] transition-colors";

/* Persoenliche 2FA-Selbstbedienung fuers KEWAG-Team (nutzt bestehende /auth/2fa-Endpunkte). */
export default function MySecurity() {
  const { t } = useI18n();
  const { user, checkAuth } = useAuth();
  const enabled = !!user?.totp_enabled;
  const remaining = user?.backup_codes_remaining;
  const [mode, setMode] = useState("idle"); // idle | setup | backupcodes | regenerate | disable
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fb; };

  // Beim Öffnen frischen Kontostand laden (u. a. korrekte Anzahl übriger Backup-Codes).
  useEffect(() => { checkAuth(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const saveCodesFile = (codes) => {
    const blob = new Blob([`KEWAGPage — 2FA Backup-Codes\n(jeder Code ist nur EINMAL gueltig — sicher aufbewahren)\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "kewagpage-2fa-backup-codes.txt"; a.click(); URL.revokeObjectURL(url);
  };
  const copyCodes = (codes) => { navigator.clipboard?.writeText(codes.join("\n")); toast.success(t("Backup-Codes kopiert")); };
  const copySecret = () => { navigator.clipboard?.writeText(secret); toast.success(t("Schlüssel kopiert")); };

  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/enable-start"); setQr(data.qr); setSecret(data.secret); setCode(""); setMode("setup"); }
    catch (e) { toast.error(errMsg(e, t("Einrichtung konnte nicht gestartet werden"))); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    if (code.trim().replace(/\s/g, "").length < 6) { toast.error(t("Bitte 6-stelligen Code aus Ihrer App eingeben")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable-confirm", { code: code.trim() });
      setBackupCodes(data.backup_codes || []);
      setQr(""); setSecret(""); setCode(""); setMode("backupcodes");
      toast.success(t("Zwei-Faktor-Login ist jetzt aktiv"));
    } catch (e) { toast.error(errMsg(e, t("Code ungültig"))); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    if (!password) { toast.error(t("Bitte Ihr Passwort eingeben")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/backup-codes/regenerate", { password });
      setBackupCodes(data.backup_codes || []); setPassword(""); setMode("backupcodes");
      toast.success(t("Neue Backup-Codes erzeugt — die alten sind jetzt ungültig"));
    } catch (e) { toast.error(errMsg(e, t("Passwort falsch"))); }
    finally { setBusy(false); }
  };
  const finishCodes = async () => { setBackupCodes([]); setMode("idle"); await checkAuth(); };
  const disable = async () => {
    if (!password) { toast.error(t("Bitte Ihr Passwort eingeben")); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password });
      toast.success(t("Zwei-Faktor-Login wurde deaktiviert"));
      setMode("idle"); setPassword(""); await checkAuth();
    } catch (e) { toast.error(errMsg(e, t("Passwort falsch"))); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-my-security">
      <div className="mb-6">
        <h1 className="text-2xl font-black tracking-tight">{t("Meine Sicherheit")}</h1>
        <p className="text-sm text-gray-400 mt-1">{t("Verwalten Sie Ihren Zwei-Faktor-Login und Ihre Backup-Codes für das Control OS.")}</p>
      </div>

      <Card className="p-6 sm:p-7" data-testid="twofa-card">
        <div className="flex items-start gap-2.5 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-[#A3E635]/15 border border-[#A3E635]/30 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-[#A3E635]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-lg font-bold">{t("Zwei-Faktor-Login (2FA)")}</h2>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border" data-testid="twofa-status-badge"
                style={{ borderColor: `${accent}66`, background: `${accent}1A`, color: accent }}>
                {enabled ? <><ShieldCheck className="w-3.5 h-3.5" /> {t("Aktiv")}</> : t("Für das Team vorgeschrieben")}
              </span>
            </div>
            <p className="text-sm text-gray-400 mt-1.5">{t("Zusätzlicher Schutz über eine Authenticator-App (z. B. Google Authenticator, Authy).")}</p>
          </div>
        </div>

        {/* IDLE */}
        {mode === "idle" && (
          <div className="mt-5 flex items-end justify-between gap-4 flex-wrap">
            <p className="text-sm text-gray-400 flex-1 min-w-[220px]">
              {enabled ? t("Ihr Konto ist mit Zwei-Faktor-Login geschützt. Beim nächsten Login fragen wir zusätzlich einen Code aus Ihrer App ab.")
                : t("2FA ist für alle KEWAG-Team-Konten Pflicht. Richten Sie es jetzt ein — Sie brauchen nur eine Authenticator-App auf Ihrem Handy.")}
            </p>
            {enabled ? (
              <div className="flex items-center gap-2.5 flex-wrap">
                {typeof remaining === "number" && (
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${remaining <= 2 ? "border-amber-400/40 bg-amber-400/10 text-amber-300" : "border-white/10 bg-white/5 text-gray-300"}`} data-testid="twofa-backup-remaining">
                    {t("Backup-Codes übrig:")} {remaining}
                  </span>
                )}
                <button onClick={() => { setPassword(""); setMode("regenerate"); }} disabled={busy} data-testid="twofa-regen-btn"
                  className="border border-white/15 bg-white/5 hover:bg-white/10 text-gray-200 font-semibold py-2.5 px-4 rounded-xl text-sm disabled:opacity-60 transition-colors">
                  {t("Backup-Codes neu erzeugen")}
                </button>
                <button onClick={() => setMode("disable")} disabled={busy} data-testid="twofa-deactivate-btn"
                  className="border border-red-400/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-semibold py-2.5 px-5 rounded-xl text-sm disabled:opacity-60 transition-colors">
                  {t("2FA deaktivieren")}
                </button>
              </div>
            ) : (
              <button onClick={start} disabled={busy} data-testid="twofa-activate-btn"
                className="font-bold py-2.5 px-5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-60 hover:opacity-90 transition-opacity"
                style={{ background: accent, color: "#071005" }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("2FA jetzt einrichten")}
              </button>
            )}
          </div>
        )}

        {/* SETUP */}
        {mode === "setup" && (
          <div className="mt-5 flex flex-col sm:flex-row gap-5 items-start" data-testid="twofa-setup-panel">
            <div className="bg-white p-3 rounded-xl shrink-0">
              {qr ? <img src={qr} alt={t("QR-Code für 2FA")} className="w-36 h-36" data-testid="twofa-qr" /> : <QrCode className="w-36 h-36 text-black" />}
            </div>
            <div className="flex-1 space-y-3 min-w-0">
              <p className="text-sm text-gray-300">{t("1. Öffnen Sie Ihre Authenticator-App und scannen Sie den QR-Code.")}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-black border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-gray-300 break-all" data-testid="twofa-secret">{secret}</code>
                <button onClick={copySecret} data-testid="twofa-copy-secret" className="bg-white/5 border border-white/10 hover:bg-white/10 text-white p-2 rounded-lg shrink-0"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-sm text-gray-300">{t("2. Geben Sie den 6-stelligen Code aus der App ein:")}</p>
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                inputMode="numeric" placeholder="000000" data-testid="twofa-code-input"
                className="w-40 bg-black border border-white/10 rounded-lg px-3 py-2.5 text-lg tracking-[0.3em] font-mono text-center focus:outline-none" style={{ caretColor: accent }} />
              <div className="flex items-center gap-3 pt-1">
                <button onClick={confirm} disabled={busy} data-testid="twofa-confirm-btn"
                  className="font-bold py-2 px-5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60 hover:opacity-90" style={{ background: accent, color: "#071005" }}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("Aktivieren & bestätigen")}
                </button>
                <button onClick={() => { setMode("idle"); setQr(""); setSecret(""); setCode(""); }} disabled={busy} data-testid="twofa-cancel-btn"
                  className="text-gray-400 hover:text-white font-semibold py-2 px-3 rounded-lg text-sm">{t("Abbrechen")}</button>
              </div>
            </div>
          </div>
        )}

        {/* BACKUP-CODES (einmalige Anzeige) */}
        {mode === "backupcodes" && (
          <div className="mt-5 max-w-md" data-testid="twofa-backupcodes-panel">
            <div className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.06] p-4">
              <p className="text-sm text-amber-200 font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4" /> {t("Ihre Backup-Codes — nur jetzt sichtbar")}</p>
              <p className="text-xs text-gray-400 mt-1">{t("Bewahren Sie diese sicher auf. Jeder Code funktioniert genau einmal, falls Sie keinen Zugriff auf Ihre Authenticator-App haben.")}</p>
              <div className="mt-3 grid grid-cols-2 gap-2" data-testid="twofa-backupcodes-list">
                {backupCodes.map((c) => (
                  <code key={c} className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm font-mono tracking-wider text-center text-gray-100">{c}</code>
                ))}
              </div>
              <div className="flex items-center gap-3 mt-4">
                <button onClick={() => copyCodes(backupCodes)} data-testid="twofa-copy-codes" className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2"><Copy className="w-4 h-4" /> {t("Kopieren")}</button>
                <button onClick={() => saveCodesFile(backupCodes)} data-testid="twofa-download-codes" className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold py-2 px-4 rounded-lg text-sm flex items-center gap-2"><Download className="w-4 h-4" /> {t("Herunterladen")}</button>
              </div>
              <button onClick={finishCodes} data-testid="twofa-codes-saved-btn" className="mt-4 w-full font-bold py-2.5 px-5 rounded-xl text-sm hover:opacity-90" style={{ background: accent, color: "#071005" }}>{t("Ich habe die Codes sicher gespeichert")}</button>
            </div>
          </div>
        )}

        {/* BACKUP-CODES NEU ERZEUGEN */}
        {mode === "regenerate" && (
          <div className="mt-5 space-y-3 max-w-sm" data-testid="twofa-regen-panel">
            <p className="text-sm text-gray-300 flex items-center gap-2"><Lock className="w-4 h-4 text-amber-300" /> {t("Passwort bestätigen, um neue Backup-Codes zu erzeugen. Die alten werden dabei ungültig.")}</p>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("Ihr Passwort")} data-testid="twofa-regen-password" className={inp} />
            <div className="flex items-center gap-3">
              <button onClick={regenerate} disabled={busy} data-testid="twofa-regen-confirm-btn" className="font-bold py-2 px-5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60 hover:opacity-90" style={{ background: accent, color: "#071005" }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} {t("Neue Codes erzeugen")}
              </button>
              <button onClick={() => { setMode("idle"); setPassword(""); }} disabled={busy} data-testid="twofa-regen-cancel-btn" className="text-gray-400 hover:text-white font-semibold py-2 px-3 rounded-lg text-sm">{t("Abbrechen")}</button>
            </div>
          </div>
        )}

        {/* DISABLE */}
        {mode === "disable" && (
          <div className="mt-5 space-y-3 max-w-md" data-testid="twofa-disable-panel">
            <div className="flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 p-3">
              <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200">{t("Hinweis: 2FA ist für Team-Konten vorgeschrieben. Nach dem Deaktivieren werden Sie beim nächsten Login erneut zur Einrichtung aufgefordert.")}</p>
            </div>
            <p className="text-sm text-gray-300 flex items-center gap-2"><Lock className="w-4 h-4 text-amber-300" /> {t("Bitte bestätigen Sie mit Ihrem Passwort, um 2FA zu deaktivieren.")}</p>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("Ihr Passwort")} data-testid="twofa-disable-password" className={inp} />
            <div className="flex items-center gap-3">
              <button onClick={disable} disabled={busy} data-testid="twofa-disable-confirm-btn"
                className="border border-red-400/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-bold py-2 px-5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t("2FA deaktivieren")}
              </button>
              <button onClick={() => { setMode("idle"); setPassword(""); }} disabled={busy} data-testid="twofa-disable-cancel-btn" className="text-gray-400 hover:text-white font-semibold py-2 px-3 rounded-lg text-sm">{t("Abbrechen")}</button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
